import { Box, Button, Container, Stack } from '@mui/material'
import { InitializationWrapper } from '@planning/components/InitializationWrapper'
import { useTranslate } from '@tolgee/react'
import {
    ColoredLabel,
    Header,
    TomTable,
    tomTableColumnFilterPresets,
    useDefaultMRTOptions,
    useMinimalsTheme,
    useTomTable,
} from '@tom-ui/ui'
import { PATHS, useAsyncFetch, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'
import { HoldTypeStatusChip } from './atoms/HoldTypeStatusChip'
import { HoldTypeTableData, mapToTableData } from './models/holdType.model'
import { HoldTypeRowActions } from './molecules/HoldTypeRowActions'
import { HoldTypesViewStore } from './stores/HoldTypesViewStore'

export interface RoleOption {
  value: number
  label: string
}

interface Props {
  userRoleOptions?: RoleOption[]
}

export const HoldTypesPage = observer(({ userRoleOptions = [] }: Props) => {
  const viewStore = useMemo(() => new HoldTypesViewStore(), [])

  const { loading: isInitializing } = useAsyncFetch(async () => {
    await viewStore.fetch()
  }, [viewStore])

  const { t } = useTranslate()
  const navigate = useNavigate()
  const theme = useMinimalsTheme()
  const defaultOptions = useDefaultMRTOptions<HoldTypeTableData>()

  const title = t('holdTypes', 'Hold types')
  const breadcrumbs = [
    { label: t(PATHS.masterData.title, 'Master data'), route: PATHS.masterData.url },
    { label: title },
  ]

  const selectFilterProps = {
    muiFilterTextFieldProps: {
      slotProps: {
        select: { sx: { minWidth: 180, height: 37 } },
      },
    },
  }

  const translatedRoleOptions = useMemo(
    () =>
      userRoleOptions.map(opt => ({
        label: t(opt.label, opt.label),
        value: opt.value,
      })),
    [userRoleOptions, t],
  )

  const reactionOptions = useMemo(
    () => [
      { label: t('Block', 'Block'), value: 'Block' },
      { label: t('Warning', 'Warning'), value: 'Warning' },
    ],
    [t],
  )

  const columns = useMemo<MRT_ColumnDef<HoldTypeTableData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('type', 'Type'),
        size: 100,
        ...tomTableColumnFilterPresets.searchMandatory<HoldTypeTableData>(),
      },
      {
        accessorKey: 'description',
        header: t('description', 'Description'),
        size: 100,
        maxSize: 100,
        ...tomTableColumnFilterPresets.searchOptional<HoldTypeTableData>(),
      },
      {
        accessorKey: 'entryRestriction',
        header: t('entry.restriction', 'Entry'),
        size: 100,
        filterVariant: 'select',
        filterSelectOptions: reactionOptions,
        enableColumnFilterModes: false,
        ...selectFilterProps,
        Cell: ({ row }) => {
          const reaction = row.original.entryRestriction
          if (!reaction) return <>{'—'}</>
          return (
            <Stack justifyContent='center' alignItems='flex-start'>
              <ColoredLabel label={t(reaction, reaction)} color='secondary' />
            </Stack>
          )
        },
      },
      {
        accessorKey: 'exitRestriction',
        header: t('exit.restriction', 'Exit'),
        size: 100,
        filterVariant: 'select',
        filterSelectOptions: reactionOptions,
        enableColumnFilterModes: false,
        ...selectFilterProps,
        Cell: ({ row }) => {
          const reaction = row.original.exitRestriction
          if (!reaction) return <>{'—'}</>
          return (
            <Stack justifyContent='center' alignItems='flex-start'>
              <ColoredLabel label={t(reaction, reaction)} color='secondary' />
            </Stack>
          )
        },
      },
      {
        accessorKey: 'yardRestriction',
        header: t('yard.restriction', 'Yard'),
        size: 100,
        filterVariant: 'select',
        filterSelectOptions: reactionOptions,
        enableColumnFilterModes: false,
        ...selectFilterProps,
        Cell: ({ row }) => {
          const reaction = row.original.yardRestriction
          if (!reaction) return <>{'—'}</>
          return (
            <Stack justifyContent='center' alignItems='flex-start'>
              <ColoredLabel label={t(reaction, reaction)} color='secondary' />
            </Stack>
          )
        },
      },
      {
        accessorKey: 'userRoles',
        header: t('userRoles', 'User roles'),
        size: 140,
        filterVariant: 'select',
        filterSelectOptions: translatedRoleOptions,
        enableColumnFilterModes: false,
        ...selectFilterProps,
        filterFn: (row, _columnId, filterValue) =>
          row.original.userRoles?.includes(Number(filterValue)) ?? false,
        Cell: ({ row }) => {
          const roles = row.original.userRoles || []
          if (roles.length === 0) return <>{'—'}</>
          return (
            <Stack direction='row' gap={1} flexWrap='wrap'>
              {roles.map(roleId => {
                const label = userRoleOptions.find(o => o.value === roleId)?.label ?? String(roleId)
                return <ColoredLabel key={roleId} label={label} color='secondary' />
              })}
            </Stack>
          )
        },
      },

      {
        id: 'origin',
        header: t('origin', 'Origin'),
        accessorFn: row => (row.isDefault ? t('default', 'Default') : t('custom', 'Custom')),
        size: 140,
        filterVariant: 'select',
        filterSelectOptions: [
          { label: t('default', 'Default'), value: t('default', 'Default') },
          { label: t('custom', 'Custom'), value: t('custom', 'Custom') },
        ],
        filterFn: 'equals',
        enableColumnFilterModes: false,
        ...selectFilterProps,
        Cell: ({ row }) => (
          <Stack direction='row' gap={1} flexWrap='wrap'>
            <ColoredLabel
              label={row.original.isDefault ? t('default', 'Default') : t('custom', 'Custom')}
              color={row.original.isDefault ? 'secondary' : 'warning'}
            />
          </Stack>
        ),
      },
      {
        id: 'status',
        accessorFn: row => (row.isActive ? 'Active' : 'Inactive'),
        header: t('status', 'Status'),
        size: 140,
        enableColumnFilterModes: false,
        filterVariant: 'select',
        filterSelectOptions: [
          { label: t('active', 'Active'), value: 'Active' },
          { label: t('inactive', 'Inactive'), value: 'Inactive' },
        ],
        filterFn: 'equals',
        ...selectFilterProps,
        Cell: ({ row }) => (
          <HoldTypeStatusChip status={row.original.isActive ? 'Active' : 'Inactive'} />
        ),
      },
    ],
    [t, userRoleOptions, translatedRoleOptions, reactionOptions],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('planning/hold-types')

  const tableData = useMemo<HoldTypeTableData[]>(
    () => viewStore.items.map(mapToTableData),
    [viewStore.items],
  )

  const table = useTomTable<HoldTypeTableData>(
    {
      columns,
      data: tableData,
      getRowId: row => String(row.id),
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      initialState: {
        ...defaultOptions.initialState,
        sorting: [
          { id: 'origin', desc: true },
          { id: 'name', desc: false },
        ],
        columnFilters: [{ id: 'status', value: 'Active' }],
        columnPinning: {
          left: ['mrt-row-actions'],
        },
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 220px)',
          minHeight: 'calc(100vh - 220px)',
        },
      },
      renderRowActions: ({ row }) => (
        <HoldTypeRowActions item={row.original} viewStore={viewStore} />
      ),
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <Box sx={{ height: '100%', bgcolor: theme.palette.grey[100] }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Header
        title={title}
        breadcrumbs={breadcrumbs}
        rightRenderOption={
          <Box flex='none'>
            <Button
              variant='contained'
              onClick={() => navigate(PATHS.masterData.holdTypes.add.url)}
              data-cy='add-new-hold-type-btn'
            >
              {t('addNew', 'Add new')}
            </Button>
          </Box>
        }
      />
      <Container sx={{ paddingY: theme.customSpacing.m }}>
        <InitializationWrapper isInitializing={isInitializing}>
          <TomTable table={table} />
        </InitializationWrapper>
      </Container>
    </Box>
  )
})
